import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Smartphone, Search, PackageSearch, CalendarDays, AlertTriangle, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  FAIXAS_TIER_APARELHO, parcelasAparelho, resolverPrecoAparelho,
  type GrupoPlanoAparelho, type MatrizPrecosAparelho,
} from '@/lib/aparelhosImport';

/**
 * A lista de aparelhos, para o CONSULTOR consultar de onde estiver.
 *
 * Douglas 09/08/2026: a mesma tabela de Configurações ▸ Produtos ▸ Aparelhos,
 * só que aberta pelo chip do topo, por cima da tela em que a pessoa está — no
 * meio de uma conversa no Inbox, de uma proposta, de onde for. Configurações é
 * tela de gestor; mandar o consultor até lá para saber se tem aparelho é o que
 * fazia ele não olhar.
 *
 * ⚠️ Só LEITURA. Importar tabela/estoque e mexer no tier continuam em
 * Configurações — aqui ninguém altera nada.
 *
 * Abre filtrado por QUEM TEM ESTOQUE: são 134 modelos na tabela do mês e ~39 com
 * aparelho no depósito. O sem estoque não some, fica apagado — o consultor
 * precisa saber que o modelo existe para responder "esse eu não consigo hoje".
 */

const GRID = 'grid grid-cols-[1.7fr_1.05fr_.8fr_.75fr_.85fr_.55fr_.7fr] gap-2 px-3';

interface LinhaCatalogo {
  id: string;
  codigo: string;
  nome: string;
  valor: number;
  metadata: Record<string, unknown>;
}

interface EstoqueModelo {
  modelo_slug: string;
  quantidade: number;
  data_foto: string;
}

function fmtBRL(v: number | null | undefined): string {
  if (v == null || v <= 0) return '—';
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

// Tier no ramo da marca, não em verde/âmbar: a lei visual do CRM reserva cor
// para semáforo de verdade. A ênfase cresce do apagado (Baixo) ao bordô (Alto).
const TIER_ESTILO: Record<string, string> = {
  baixo: 'text-muted-foreground',
  medio: 'text-foreground font-semibold',
  alto: 'text-primary font-bold',
};
const TIER_ROTULO: Record<string, string> = { baixo: 'Baixo', medio: 'Médio', alto: 'Alto' };

// O preço do aparelho MUDA com o plano da linha — é o subsídio. Sem poder trocar,
// a lista respondia por um plano só e o consultor tinha que abrir a proposta pra
// saber quanto ficava nos outros. (Douglas 09/08/2026)
// ⚠️ "HIBRIDO" e' o nome da CHAVE, nao o nome que o consultor le. Conferido no
// material oficial da Claro (29/07/2026): "híbrido" NAO existe como familia de
// plano movel — as familias sao Aquisicao, Portabilidade, Renovacao e Incremento,
// Oferta Relampago, Migracao Pre→Pos, Regional, Exclusivo MG. A unica "híbrida"
// do material e' TECNOLOGIA hibrida (HFC+GPON), de fixa/TV, coisa completamente
// diferente. O nome saiu do importador, que le as colunas M–R da planilha por
// letra e nunca leu o cabecalho. Na pratica essas colunas valem pra tudo que NAO
// e' renovacao — por isso "Venda nova", palavra que a casa ja' usa
// (faturamento_tipo='VENDA NOVA'). (Douglas 09/08/2026)
const GRUPO_ROTULO: Record<string, string> = {
  RENOVACAO: 'Renovação', HIBRIDO: 'Venda nova',
  TOTAL_INDIVIDUAL: 'Total Individual', TOTAL_COMPARTILHADO: 'Total Compartilhado',
};
const GRUPO_CURTO: Record<string, string> = {
  RENOVACAO: 'RENOV.', HIBRIDO: 'VENDA NOVA',
  TOTAL_INDIVIDUAL: 'TOTAL IND.', TOTAL_COMPARTILHADO: 'TOTAL COMP.',
};
const ORDEM_GRUPO: Record<string, number> = {
  RENOVACAO: 0, HIBRIDO: 1, TOTAL_INDIVIDUAL: 2, TOTAL_COMPARTILHADO: 3,
};

interface Props {
  aberto: boolean;
  onFechar: (aberto: boolean) => void;
}

export function ModalAparelhosDisponiveis({ aberto, onFechar }: Props) {
  const [busca, setBusca] = useState('');
  const [soComEstoque, setSoComEstoque] = useState(true);
  const [plano, setPlano] = useState('RENOVACAO:100');

  // Mesmas queryKeys da aba de Configurações e do seletor da proposta: quem já
  // abriu uma delas nesta sessão vê a lista aparecer instantânea, do cache.
  const { data: catalogo = [], isLoading } = useQuery({
    queryKey: ['aparelhos-catalogo'],
    enabled: aberto,
    staleTime: 60_000,
    queryFn: async (): Promise<LinhaCatalogo[]> => {
      const { data, error } = await supabase
        .from('planos_proposta_catalogo' as never)
        .select('id, codigo, nome, valor, ativo, metadata')
        .eq('categoria', 'aparelhos')
        .order('nome');
      if (error) throw error;
      return (data || []) as unknown as LinhaCatalogo[];
    },
  });

  const { data: estoque = [] } = useQuery({
    queryKey: ['aparelhos-estoque-modelo'],
    enabled: aberto,
    staleTime: 60_000,
    queryFn: async (): Promise<EstoqueModelo[]> => {
      const { data, error } = await supabase
        .from('v_aparelhos_estoque_modelo' as never)
        .select('modelo_slug, quantidade, data_foto');
      if (error) return [];
      return (data || []) as unknown as EstoqueModelo[];
    },
  });

  const estoqueMap = useMemo(
    () => new Map(estoque.map((e) => [e.modelo_slug, e.quantidade])),
    [estoque],
  );
  const dataFoto = estoque[0]?.data_foto ?? null;
  const diasDaFoto = dataFoto
    ? Math.floor((Date.now() - new Date(`${dataFoto}T12:00:00`).getTime()) / 86_400_000)
    : null;
  const competencia = (catalogo[0]?.metadata?.competencia as string | undefined) ?? null;
  const comEstoque = useMemo(
    () => catalogo.filter((l) => (estoqueMap.get(l.codigo) ?? 0) > 0).length,
    [catalogo, estoqueMap],
  );

  // Os planos vem da propria tabela importada, nao de uma lista fixa: quando a
  // Claro mandar a tabela do mes que vem com outras faixas, o seletor acompanha
  // sozinho. Hoje (agosto) sao Renovacao 6/20/30/40/100 e Hibrido 10/15/25/50/80/150.
  const planosDisponiveis = useMemo(() => {
    const achados = new Map<string, { grupo: GrupoPlanoAparelho; gb: number }>();
    for (const l of catalogo) {
      const m = (l.metadata?.precos ?? {}) as Partial<MatrizPrecosAparelho>;
      const porGrupo: [GrupoPlanoAparelho, Record<number, number> | undefined][] = [
        ['RENOVACAO', m.renovacao], ['HIBRIDO', m.hibrido], ['TOTAL_INDIVIDUAL', m.totalIndividual],
      ];
      for (const [grupo, tabela] of porGrupo) {
        for (const gb of Object.keys(tabela ?? {})) achados.set(`${grupo}:${gb}`, { grupo, gb: Number(gb) });
      }
    }
    return [...achados.entries()]
      .map(([id, v]) => ({ id, ...v }))
      .sort((x, y) => (ORDEM_GRUPO[x.grupo] - ORDEM_GRUPO[y.grupo]) || (x.gb - y.gb));
  }, [catalogo]);

  const planoAtual = planosDisponiveis.find((p) => p.id === plano) ?? planosDisponiveis[0];

  const lista = useMemo(() => {
    const q = busca.trim().toLowerCase();
    const base = catalogo.filter((l) => {
      const qtd = estoqueMap.get(l.codigo) ?? 0;
      if (soComEstoque && qtd <= 0) return false;
      if (!q) return true;
      return l.nome.toLowerCase().includes(q)
        || String(l.metadata?.fabricante ?? '').toLowerCase().includes(q);
    });
    // Mais disponível primeiro: é a ordem de quem vai vender hoje.
    return [...base].sort((a, b) => (estoqueMap.get(b.codigo) ?? 0) - (estoqueMap.get(a.codigo) ?? 0));
  }, [catalogo, busca, soComEstoque, estoqueMap]);

  const unidadesNaLista = useMemo(
    () => lista.reduce((s, l) => s + (estoqueMap.get(l.codigo) ?? 0), 0),
    [lista, estoqueMap],
  );

  return (
    <Dialog open={aberto} onOpenChange={onFechar}>
      <DialogContent className="max-w-6xl h-[80vh] p-0 flex flex-col gap-0">
        <DialogHeader className="px-4 pt-4 pb-3 flex-none space-y-3">
          <div className="flex items-center gap-2 flex-nowrap">
            <DialogTitle className="flex items-center gap-2 text-base">
              <Smartphone className="h-4 w-4 text-primary" />
              Aparelhos
            </DialogTitle>
            <div className="ml-auto flex items-center gap-1 shrink-0">
              <Badge variant="outline" className="gap-1 text-[10px] font-normal whitespace-nowrap">
                <CalendarDays className="h-3 w-3" />
                Tabela {competencia ?? '—'}
              </Badge>
              <Badge
                variant={diasDaFoto != null && diasDaFoto > 1 ? 'destructive' : 'outline'}
                className="gap-1 text-[10px] font-normal whitespace-nowrap"
              >
                {diasDaFoto != null && diasDaFoto > 1
                  ? <AlertTriangle className="h-3 w-3" />
                  : <PackageSearch className="h-3 w-3" />}
                Estoque de {dataFoto ? dataFoto.split('-').reverse().join('/') : '—'}
                {diasDaFoto != null && diasDaFoto > 1 && ` · ${diasDaFoto} dias`}
              </Badge>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-nowrap">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder="Buscar modelo ou fabricante..."
                className="pl-8 h-8 text-sm"
                autoFocus
              />
            </div>
            {/* O plano da linha muda o preco do aparelho — e' o subsidio. Trocar
                aqui recalcula a coluna do plano E a parcela de uma vez. */}
            <Select value={planoAtual?.id ?? ''} onValueChange={setPlano}>
              <SelectTrigger className="h-8 w-[172px] text-[11px] shrink-0">
                <SelectValue placeholder="Plano" />
              </SelectTrigger>
              <SelectContent>
                {planosDisponiveis.map((pl) => (
                  <SelectItem key={pl.id} value={pl.id} className="text-xs">
                    {GRUPO_ROTULO[pl.grupo]} {pl.gb}GB
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button
              type="button"
              onClick={() => setSoComEstoque(true)}
              className={cn(
                'h-8 rounded-md px-2 text-[11px] font-medium shrink-0 whitespace-nowrap transition-colors',
                soComEstoque ? 'bg-primary text-primary-foreground' : 'border border-border hover:bg-accent',
              )}
            >
              Com estoque · {comEstoque}
            </button>
            <button
              type="button"
              onClick={() => setSoComEstoque(false)}
              className={cn(
                'h-8 rounded-md px-2 text-[11px] font-medium shrink-0 whitespace-nowrap transition-colors',
                !soComEstoque ? 'bg-primary text-primary-foreground' : 'border border-border hover:bg-accent',
              )}
            >
              Todos · {catalogo.length}
            </button>
          </div>
        </DialogHeader>

        {isLoading ? (
          <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Carregando aparelhos...
          </div>
        ) : lista.length === 0 ? (
          <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground px-6 text-center">
            {catalogo.length === 0
              ? 'A tabela do mês ainda não foi importada.'
              : busca
                ? `Nenhum aparelho com "${busca}"${soComEstoque ? ' em estoque. Toque em "Todos" para ver o catálogo inteiro.' : '.'}`
                : 'Nenhum aparelho em estoque na foto de hoje.'}
          </div>
        ) : (
          <>
            <div className="flex-1 min-h-0 overflow-y-auto">
              <div className={cn(
                GRID,
                'sticky top-0 z-10 bg-muted h-6 items-center text-xs font-bold uppercase tracking-wider text-muted-foreground',
              )}>
                <span className="truncate">Modelo</span>
                <span className="truncate">Fabricante · Gama</span>
                <span className="truncate text-right">À vista</span>
                <span className="truncate text-right">
                  {planoAtual ? `${GRUPO_CURTO[planoAtual.grupo]} ${planoAtual.gb}GB` : 'Plano'}
                </span>
                <span className="truncate text-right">24×</span>
                <span className="truncate text-center">Tier</span>
                <span className="truncate text-right">Estoque</span>
              </div>

              {lista.map((l) => {
                const qtd = estoqueMap.get(l.codigo) ?? 0;
                const precos = (l.metadata?.precos ?? {}) as { renovacao?: Record<string, number> };
                const tier = String(l.metadata?.tier ?? '');
                const fabricante = String(l.metadata?.fabricante ?? '');
                // ⛔ A parcela NAO sai do "a' vista". Regra da Claro em
                // resolverPrecoAparelho: a' vista e' SEMPRE o preco base, sem
                // subsidio, e o parcelado NUNCA usa esse preco — usa o do plano.
                // Dividir o base por 24 cotava quase o dobro do real (A07: R$ 41,63
                // em vez de R$ 24,96) e fazia o consultor perder venda.
                // (Douglas achou em 09/08, no mesmo dia em que subiu)
                //
                // Uso a MESMA funcao da proposta, entao a regra conservadora vem de
                // graca: a faixa usada e' a MAIOR que o cliente alcanca, nunca uma
                // que ele nao chegou. Se o modelo nao tem preco naquele plano, ela
                // cai no base — e ai' marco como "sem subsidio", em vez de fingir
                // que aquele e' o preco do plano.
                const resolvido = planoAtual
                  ? resolverPrecoAparelho(precos as MatrizPrecosAparelho, planoAtual.grupo, planoAtual.gb, '24x')
                  : null;
                const precoPlano = resolvido?.preco ?? null;
                const semSubsidio = !!resolvido && !resolvido.subsidiado;
                const parcela = precoPlano && precoPlano > 0 ? parcelasAparelho(precoPlano, '24x') : null;
                const gama = String(l.metadata?.gama ?? '');
                return (
                  <div
                    key={l.id}
                    className={cn(
                      GRID,
                      'h-7 items-center overflow-hidden text-sm border-b border-border/50 hover:bg-accent/40',
                      qtd <= 0 && 'text-muted-foreground',
                    )}
                  >
                    <span className={cn('truncate', qtd > 0 && 'font-medium')} title={l.nome}>{l.nome}</span>
                    <span className="truncate text-muted-foreground" title={`${fabricante} · ${gama}`}>
                      {fabricante} · {gama}
                    </span>
                    <span className="truncate text-right tabular-nums text-primary">{fmtBRL(l.valor)}</span>
                    <span
                      className={cn('truncate text-right tabular-nums', semSubsidio ? 'text-muted-foreground' : 'text-primary')}
                      title={semSubsidio
                        ? 'Esse modelo não tem preço nesse plano na tabela do mês — cai no preço base, sem subsídio.'
                        : undefined}
                    >
                      {fmtBRL(precoPlano)}
                    </span>
                    {/* Mesma conta da proposta (parcelasAparelho), sobre o preco do
                        PLANO — pra o numero que o consultor fala no telefone ser o
                        que sai na proposta depois. */}
                    <span
                      className="truncate text-right tabular-nums text-primary"
                      title={parcela
                        ? `24× de ${fmtBRL(parcela.valor)} sobre ${fmtBRL(precoPlano)}${
                            planoAtual ? ` — ${GRUPO_ROTULO[planoAtual.grupo]} ${planoAtual.gb}GB` : ''
                          }${resolvido?.faixaGb && planoAtual && resolvido.faixaGb !== planoAtual.gb
                            ? ` (usa a faixa de ${resolvido.faixaGb}GB, a maior que o plano alcança)` : ''}`
                        : 'Sem preço nesse plano na tabela do mês.'}
                    >
                      {parcela ? fmtBRL(parcela.valor) : '—'}
                    </span>
                    <span className={cn('truncate text-center text-xs', TIER_ESTILO[tier] ?? 'text-muted-foreground')}>
                      {TIER_ROTULO[tier] ?? '—'}
                    </span>
                    <span className="truncate text-right tabular-nums">
                      {qtd > 0
                        ? <span className="font-bold text-primary">{qtd.toLocaleString('pt-BR')} un</span>
                        : <span className="text-muted-foreground">sem estoque</span>}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* TOTAL fora do scroll, como manda a lei da tabela do CRM: some ao
                rolar é justamente quando mais se quer olhar. */}
            <div className={cn(
              GRID,
              'flex-none h-7 items-center bg-muted border-t-2 border-border text-sm font-bold',
            )}>
              <span className="truncate">
                {lista.length.toLocaleString('pt-BR')} {lista.length === 1 ? 'modelo' : 'modelos'}
              </span>
              <span className="truncate text-[10px] font-normal text-muted-foreground col-span-5 self-center">
                À vista = preço base, sem subsídio · 24× = sobre o preço do plano escolhido (parcelado só existe com plano) · Tier:
                {' '}Baixo até {fmtBRL(FAIXAS_TIER_APARELHO.medio - 1)} ·
                {' '}Médio até {fmtBRL(FAIXAS_TIER_APARELHO.alto - 1)} · Alto acima.
              </span>
              <span className="truncate text-right tabular-nums text-primary">
                {unidadesNaLista.toLocaleString('pt-BR')} un
              </span>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
